/** AI Insights slice — the reference aiModel() (design-reference/PointFive OS Original.dc.html, script lines 1475–1635):
 *  style identity banner, period + account selectors, four analysis-type launchers, the structured analysis panel
 *  (equity trajectory, discipline signals, risk flags, what improved / regressed, metric deltas, action plan) and the
 *  saved-insight list. Behaviour and strings verbatim; entity data comes from `c.data.ai` and the shared ledger `c.trades()`. */
import type { Ctx } from './ctx';
import type { ViewModel } from '@/screens/generated/view-model';
import type { TradeTuple } from './helpers';

type FlagSev = 'warning' | 'critical';
interface Flag {
  sev: FlagSev;
  title: string;
  detail: string;
}
interface FlagTone {
  fg: string;
  bd: string;
  bg: string;
}
interface Delta {
  label: string;
  now: string;
  was: string;
  d: string;
  fg: string;
  icon: string;
}
interface AnalysisType {
  type: string;
  label: string;
  icon: string;
  desc: string;
}

const SPAN: Record<string, number> = { today: 0, week: 6, month: 30 };

// Four analysis-type launchers — UI copy, stays in the slice
// data-lint-allow: analysis-launcher vocabulary (labels + icons) — the reference’s own copy, not entity data
const TYPES: AnalysisType[] = [
  {
    type: 'behavior',
    label: 'Behaviour',
    icon: 'ph-brain',
    desc: 'Emotional patterns, discipline, and psychological tendencies — evaluated against your style norms',
  },
  {
    type: 'strategy',
    label: 'Strategy',
    icon: 'ph-trend-up',
    desc: 'Setup quality, entry/exit timing, and pattern consistency for your declared trading approach',
  },
  {
    type: 'suggestion',
    label: 'Suggestions',
    icon: 'ph-sparkle',
    desc: 'Actionable improvements tailored to your style, experience level, and stated goals',
  },
  {
    type: 'performance',
    label: 'Full Analysis',
    icon: 'ph-target',
    desc: 'Comprehensive review of all metrics benchmarked against your trading identity',
  },
];

const PERIODS: [string, string, string][] = [
  ['today', 'Today', 'ph-sun'],
  ['week', 'This Week', 'ph-trend-up'],
  ['month', 'This Month', 'ph-stack'],
];

export function buildAi(c: Ctx) {
  const S = c.S;
  const D = c.data.ai;
  const money = (v: number) =>
    (v >= 0 ? '+$' : '−$') +
    Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // An unknown period id leaves every window comparison false, exactly as the reference's undefined SPAN lookup does
  const span = SPAN[S.aiPeriod] ?? NaN;
  const all = c.trades();
  const rows = all.filter((r) => r[1] <= span && (S.aiAcct === 'all' || r[2] === S.aiAcct));
  const wins = rows.filter((r) => r[8] > 0),
    losses = rows.filter((r) => r[8] < 0);
  const net = rows.reduce((a, r) => a + r[8], 0);
  const wr = rows.length ? (wins.length / rows.length) * 100 : 0;
  const gw = wins.reduce((a, r) => a + r[8], 0),
    gl = Math.abs(losses.reduce((a, r) => a + r[8], 0));
  const pf = gl ? gw / gl : 0;
  const offPlan = rows.filter((r) => D.playbook.indexOf(r[10]) < 0);
  const asia = rows.filter((r) => parseInt(String(r[12]).slice(0, 2), 10) < 7);
  const startBal = D.equity - net;
  const growth = startBal ? (net / startBal) * 100 : 0;
  const positive = net >= 0;

  // 7-day metric deltas against the prior window
  const prior = all.filter((r) => r[1] > span && r[1] <= span * 2 + 1);
  const pWins = prior.filter((r) => r[8] > 0);
  const pWr = prior.length ? (pWins.length / prior.length) * 100 : 0;
  const pNet = prior.reduce((a, r) => a + r[8], 0);
  const pGl = Math.abs(prior.filter((r) => r[8] < 0).reduce((a, r) => a + r[8], 0));
  const pGw = pWins.reduce((a, r) => a + r[8], 0);
  const pPf: number | null = pGl ? pGw / pGl : null; // null = no losses, so undefined rather than 0
  const pfStr = (v: number | null) => (v === null ? '∞' : v ? v.toFixed(2) : '—');
  const pfComparable = pPf !== null && pf > 0;
  const delta = (
    label: string,
    now: number,
    was: number,
    fmt: (v: number) => string,
    better: 'higher' | 'lower',
  ): Delta => {
    const d = now - was;
    if (d === 0) {
      // unchanged is neither up nor good
      return { label, now: fmt(now), was: fmt(was), d: 'no change', fg: 'var(--muted2)', icon: 'ph-minus' };
    }
    const good = better === 'higher' ? d > 0 : d < 0;
    return {
      label,
      now: fmt(now),
      was: fmt(was),
      d: (d > 0 ? '+' : '−') + fmt(Math.abs(d)).replace('+', ''),
      fg: good ? 'var(--pos)' : 'var(--neg)',
      icon: d > 0 ? 'ph-arrow-up-right' : 'ph-arrow-down-right',
    };
  };

  const offPlanPnl = (list: TradeTuple[]) => list.reduce((a, r) => a + r[8], 0);
  const flags: Flag[] = [];
  if (offPlan.length)
    flags.push({
      sev: 'warning',
      title: offPlan.length + (offPlan.length === 1 ? ' entry' : ' entries') + ' outside your armed playbook',
      detail:
        offPlan.map((r) => r[3] + ' · ' + r[10]).join(', ') + ' — off-plan P&L ' + money(offPlanPnl(offPlan)),
    });
  if (asia.length)
    flags.push({
      sev: 'warning',
      title: asia.length + ' pre-07:00 entry' + (asia.length === 1 ? '' : 's'),
      detail: 'Taken outside your declared 07:00–16:00 London window',
    });
  if (pf && pf < 1)
    flags.push({
      sev: 'critical',
      title: 'Profit factor below 1.00',
      detail: 'Gross loss exceeds gross profit over this period',
    });
  const flagTone = (s: FlagSev): FlagTone =>
    s === 'critical'
      ? {
          fg: 'var(--neg)',
          bd: 'color-mix(in srgb, var(--neg) 28%, transparent)',
          bg: 'color-mix(in srgb, var(--neg-fill) 7%, transparent)',
        }
      : {
          fg: 'var(--amb-2)',
          bd: 'color-mix(in srgb, var(--amb) 28%, transparent)',
          bg: 'color-mix(in srgb, var(--amb) 7%, transparent)',
        };

  const signalCount = (() => {
    let n = 0;
    if (rows.length && !offPlan.length) n++;
    if (wr >= 60) n++;
    if (pf >= 2) n++;
    if (!asia.length && rows.length) n++;
    return n;
  })();
  const projected = growth * (30 / Math.max(1, span || 1));
  const firstOffPlan = offPlan[0];

  return {
    aiUnread: '2 new',
    // aiStyleSet (reference key) omitted — the template never binds it
    aiStyleLabel: 'Intraday Reversal Trader',
    aiStyleTip:
      'Evaluated against intraday norms — a 2-per-session cap and a 07:00–16:00 London window, so trade count is not read as overtrading.',
    aiStyleEdit: () => c.flash('Trading identity opened for editing', 'mut'),
    aiPeriods: PERIODS.map(([id, label, icon]) => ({
      label,
      icon,
      go: () => c.set({ aiPeriod: id }),
      bg: S.aiPeriod === id ? 'var(--rail)' : 'transparent',
      fg: S.aiPeriod === id ? 'var(--rail-ink)' : 'var(--muted)',
      bd: S.aiPeriod === id ? 'var(--rail)' : 'var(--l10)',
    })),
    aiAccts: D.accounts.map(([id, label]) => ({
      label,
      go: () => c.set({ aiAcct: id }),
      bg: S.aiAcct === id ? 'var(--l6)' : 'transparent',
      fg: S.aiAcct === id ? 'var(--text)' : 'var(--muted)',
      bd: S.aiAcct === id ? 'color-mix(in srgb, var(--acc-fill) 45%, transparent)' : 'var(--l10)',
    })),
    aiTypes: TYPES.map((t) => ({
      label: t.label,
      icon: t.icon,
      desc: t.desc,
      running: S.aiRunning === t.type,
      badge: S.aiRunning === t.type ? 'Analysing…' : '',
      go: () => {
        if (S.aiRunning) return;
        c.set({ aiRunning: t.type });
        c.timers.after(950, () => {
          c.set({ aiRunning: null });
          c.flash(t.label + ' analysis generated', 'pos');
        });
      },
    })),
    // aiRunningAny (reference key) omitted — the template never binds it
    // structured analysis panel
    aiTrajLabel: positive ? 'Compounding' : 'Drawdown',
    aiTrajFg: positive ? 'var(--pos)' : 'var(--amb-2)',
    aiTrajBd: positive
      ? 'color-mix(in srgb, var(--pos) 28%, transparent)'
      : 'color-mix(in srgb, var(--amb) 28%, transparent)',
    aiTrajBg: positive
      ? 'color-mix(in srgb, var(--pos) 6%, transparent)'
      : 'color-mix(in srgb, var(--amb) 6%, transparent)',
    aiGrowth: (growth >= 0 ? '+' : '−') + Math.abs(growth).toFixed(2) + '%',
    aiTrajSummary: rows.length
      ? 'Equity moved ' +
        money(net) +
        ' across ' +
        rows.length +
        (rows.length === 1 ? ' trade' : ' trades') +
        ' at a ' +
        wr.toFixed(0) +
        '% win rate and a ' +
        (pf ? pf.toFixed(2) : '—') +
        ' profit factor. ' +
        (offPlan.length
          ? 'The off-plan entries account for ' + money(offPlanPnl(offPlan)) + ' of that.'
          : 'Every entry sat inside your armed playbook.')
      : 'No trades in this period, so there is nothing to analyse yet.',
    aiProjected: (projected >= 0 ? '+' : '−') + Math.abs(projected).toFixed(2) + '%',
    aiSignals: (() => {
      const out: { signal: string; detail: string }[] = [];
      if (rows.length && !offPlan.length)
        out.push({
          signal: 'Every entry tagged to an armed strategy',
          detail:
            'Plan adherence held at 100% across ' +
            rows.length +
            (rows.length === 1 ? ' trade' : ' trades') +
            '.',
        });
      if (wr >= 60)
        out.push({
          signal: 'Win rate above your 60% benchmark',
          detail: wins.length + ' of ' + rows.length + ' entries closed green.',
        });
      if (pf >= 2)
        out.push({
          signal: 'Profit factor above 2.00',
          detail: 'Gross profit is ' + pf.toFixed(2) + '× gross loss for the period.',
        });
      if (!asia.length && rows.length)
        out.push({
          signal: 'No entries outside the declared window',
          detail: 'All fills landed inside 07:00–16:00 London.',
        });
      return out;
    })(),
    aiHasSignals: signalCount > 0,
    aiSignalCount:
      signalCount + (signalCount === 1 ? ' positive pattern detected' : ' positive patterns detected'),
    aiFlags: flags.map((f) =>
      Object.assign({ title: f.title, detail: f.detail, sev: f.sev }, flagTone(f.sev)),
    ),
    aiHasFlags: flags.length > 0,
    aiFlagCount: flags.length + (flags.length === 1 ? ' flag raised' : ' flags raised'),
    aiImproved: (() => {
      const out: { t: string }[] = [];
      if (wr > pWr)
        out.push({ t: 'Win rate up ' + (wr - pWr).toFixed(1) + ' points against the previous period' });
      if (net > pNet) out.push({ t: 'Net P&L improved by ' + money(net - pNet) });
      if (pfComparable && pPf !== null && pf > pPf)
        out.push({ t: 'Profit factor up from ' + pfStr(pPf) + ' to ' + pfStr(pf) });
      return out;
    })(),
    aiRegressed: (() => {
      const out: { t: string }[] = [];
      if (wr < pWr)
        out.push({ t: 'Win rate down ' + (pWr - wr).toFixed(1) + ' points against the previous period' });
      if (pPf === null && prior.length)
        out.push({ t: 'The previous period took no losses, so profit factor has no comparable baseline' });
      if (net < pNet) out.push({ t: 'Net P&L fell by ' + money(pNet - net) });
      if (offPlan.length)
        out.push({
          t:
            offPlan.length +
            ' off-plan ' +
            (offPlan.length === 1 ? 'entry' : 'entries') +
            ' where the previous period had none',
        });
      return out;
    })(),
    aiDeltas:
      rows.length && prior.length
        ? [
            delta('Win rate', wr, pWr, (v) => v.toFixed(1) + '%', 'higher'),
            delta(
              'Net P&L',
              net,
              pNet,
              (v) => (v < 0 ? '−$' : '$') + Math.abs(Math.round(v)).toLocaleString('en-US'),
              'higher',
            ),
            pfComparable && pPf !== null
              ? delta('Profit factor', pf, pPf, (v) => v.toFixed(2), 'higher')
              : {
                  label: 'Profit factor',
                  now: pfStr(pf),
                  was: pfStr(pPf),
                  d: 'no baseline',
                  fg: 'var(--muted2)',
                  icon: 'ph-minus',
                },
            delta('Trades', rows.length, prior.length, (v) => String(Math.round(v)), 'higher'),
          ]
        : [],
    aiHasDeltas: rows.length > 0 && prior.length > 0,
    aiActions: (() => {
      const out: { n: string; t: string }[] = [];
      if (firstOffPlan)
        out.push({
          n: '1',
          t: 'Arm or drop ' + firstOffPlan[10] + ' — it traded without being on the session plan.',
        });
      if (asia.length)
        out.push({
          n: String(out.length + 1),
          t: 'Enable the session-window lock so pre-07:00 fills are rejected rather than reviewed after the fact.',
        });
      out.push({
        n: String(out.length + 1),
        t: 'Hold the 2-per-session cap on the London book; expectancy falls once you exceed it.',
      });
      return out;
    })(),
    aiSendForward: () => c.flash('Action plan pushed to your next session setup', 'pos'),
    aiInsights: D.insights.map((i, idx) => ({
      title: i.title,
      when: i.when,
      body: i.body,
      refs: i.refs.map((r) => ({ label: r })),
      sev: i.sev,
      sevFg: i.sev === 'warning' ? 'var(--amb-2)' : 'var(--sky)',
      sevBg:
        i.sev === 'warning'
          ? 'color-mix(in srgb, var(--amb) 12%, transparent)'
          : 'color-mix(in srgb, var(--sky-fill) 12%, transparent)',
      icon: i.sev === 'warning' ? 'ph-warning' : 'ph-lightbulb',
      markBd: i.unread ? 'var(--acc-fill)' : 'transparent',
      open: S.aiExpanded === idx,
      caret: S.aiExpanded === idx ? 'rotate(180deg)' : 'rotate(0deg)',
      toggle: () => c.set((s) => ({ aiExpanded: s.aiExpanded === idx ? -1 : idx })),
    })),
    aiEmpty: rows.length === 0,
  } satisfies Partial<ViewModel>;
}
export type AiModel = ReturnType<typeof buildAi>;
